// CLAIM-WORKSPACE-UX-1 (2026-06-02) — container components that consolidate the
// old 14-tab ClaimDetail into the 7-tab claim workspace. These are thin wrappers:
// they render the EXISTING tab components (unchanged) under a pill sub-nav, so all
// API calls, state, permission gates, financial math, and the TPA wall are
// preserved. No backend changes. Tab map:
//   Overview   = GeneralTab + at-a-glance cards
//   Activity   = Feed (ActivityTab) / Notes (DiaryTab) / Tasks (TasksTab)
//   Parties    = PartiesTab + Claimant & Loss (ClaimantTab)
//   Financials = Reserves / Payments / Bills / Time & Expenses (+ Reimbursements
//                & TPA Financials seams for the next build)
//   Reports    = ClaimReportsTab + cap-gated Bordereau
import { useState, useEffect } from "react";
import { apiFetch, fmt, can } from "../api";
import {
  GeneralTab, PartiesTab, ReservesTab, PaymentsTab, TasksTab,
  TimeExpensesTab,
} from "./ClaimTabs";
import ClaimantTab from "./ClaimantTab";
import PolicyCoverageTab from "./PolicyCoverageTab";
import ActivityTab, { FILTERS as ACT_FILTERS, TYPE_META as ACT_TYPE_META } from "./ActivityTab";
import DiaryTab from "./DiaryTab";
import ClaimReportsTab from "./ClaimReportsTab";
import BordereauClaimTab from "./BordereauClaimTab";
import { ClaimBillsTab } from "./BillIntake";
import ReimbursementsPanel from "./Reimbursements";
import PaymentRequests from "./PaymentRequests";
import BillingReadiness from "./BillingReadiness";
import { ClaimReadinessPanel } from "./ClaimReadiness";
import ClaimAttentionPanel from "./ClaimAttentionPanel";
// UI-STANDARD-1 (2026-07-02): SubNav / OverviewTile / KeyValue / SummaryRibbon
// primitives extracted to ui.js (Design Standard Part V) — this file was their
// origin; it now consumes the shared kit. Rendering is unchanged.
import { StatusPill, Money, SubNav, OverviewTile, KeyValue as KV, SummaryRibbon, RibbonCell } from "./ui";

function InfoPanel({ title, children }) {
  return (
    <div className="card"><div className="card-body" style={{ padding: "28px 24px", color: "var(--text3)", fontSize: 13, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>{title}</div>
      {children}
    </div></div>
  );
}

// ── Overview (CLAIM-OVERVIEW-UX-2, 2026-06-18) — command rail + dark-header blocks
// Left "command rail" = the designated operational space (quick actions, next
// actions/attention, readiness). Right = densely-packed, dark-headered summary
// blocks (shared OverviewTile); every block header deep-links to where the
// adjuster acts. Cockpit rule (LD-DESIGN-001): tiles orient + deep-link only.
const Bubble = ({ pct, name, lead }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12.5 }}>
    <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                   background: lead ? "var(--navy)" : "#eef2ff", color: lead ? "#fff" : "var(--navy)", fontWeight: 700, fontSize: 10.5 }}>
      {pct != null ? Math.round(Number(pct)) + "%" : "—"}</span>
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {name || "—"}{lead && <span style={{ marginLeft: 5, fontSize: 9, color: "#991b1b", fontWeight: 700 }}>LEAD</span>}</span>
  </div>
);
const QBtn = ({ icon, label, onClick }) => (
  <button onClick={onClick} className="btn btn-outline btn-sm"
    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "8px 4px", fontSize: 10.5, lineHeight: 1.1 }}>
    <span style={{ fontSize: 15 }}>{icon}</span>{label}
  </button>
);
const yn = (v) => v ? <StatusPill sm status="yes">Yes</StatusPill> : <span style={{ color: "var(--text3)" }}>No</span>;
const PRETTY = { off_work: "Off Work", light_duty: "Light Duty", returned_to_work: "Returned to Work", working: "Working", terminated: "Terminated", unknown: "Unknown", suit_filed: "Suit Filed", in_litigation: "In Litigation", represented_pre_suit: "Represented (pre-suit)", not_represented: "Not Represented", settled: "Settled", closed: "Closed" };
const pretty = (v) => v ? (PRETTY[v] || v) : null;

export function OverviewTab({ claim, vca, onClaimUpdated, claimId, openTask, openTime, claimLabel, workRefresh, goTab, onAttnGo }) {
  const [bills, setBills] = useState([]);
  const [fin, setFin] = useState(null);
  const [claimant, setClaimant] = useState(null);
  const [attn, setAttn] = useState([]);
  const [parties, setParties] = useState([]);
  const [pol, setPol] = useState(null);
  const [time, setTime] = useState([]);
  const [timeLoaded, setTimeLoaded] = useState(false);  // A1: distinguish loading from a true zero
  // LD-DESIGN-012 pattern 5: Recent Activity tile with count-bearing filter pills.
  const [activity, setActivity] = useState([]);
  const [actFilter, setActFilter] = useState("all");
  useEffect(() => { apiFetch(`/claims/${claimId}/bills`).then(d => setBills(d || [])).catch(() => setBills([])); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/reserves`).then(d => { const r = d?.reserves || []; setFin({ total: r.reduce((s, x) => s + parseFloat(x.total_reserve || 0), 0), paid: r.reduce((s, x) => s + parseFloat(x.cum_paid || 0), 0), rem: r.reduce((s, x) => s + parseFloat(x.current_balance || 0), 0) }); }).catch(() => setFin({ total: 0, paid: 0, rem: 0 })); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/claimant`).then(setClaimant).catch(() => setClaimant(null)); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/attention`).then(d => setAttn(d || [])).catch(() => setAttn([])); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/parties`).then(d => setParties(d || [])).catch(() => setParties([])); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/policy-coverage`).then(setPol).catch(() => setPol(null)); }, [claimId, workRefresh]);
  useEffect(() => { setTimeLoaded(false); apiFetch(`/claims/${claimId}/time`).then(d => setTime(d || [])).catch(() => setTime([])).finally(() => setTimeLoaded(true)); }, [claimId, workRefresh]);
  useEffect(() => { apiFetch(`/claims/${claimId}/activity`).then(d => setActivity(d || [])).catch(() => setActivity([])); }, [claimId, workRefresh]);

  const num = (v) => parseFloat(v || 0);
  const openBills = (bills || []).filter(b => ["Needs Claim", "Needs Coding", "Adjuster Review", "Request Info", "Hold"].includes(b.current_status)).length;
  const cd = claimant?.materialized ? claimant.claimant : (claimant?.fallback || {});
  const claimantName = cd?.display_name || [cd?.first_name, cd?.last_name].filter(Boolean).join(" ") || claim?.insured_name || "—";
  const missing = [];
  if (claimant) {
    if (!cd?.date_of_birth && cd?.claimant_type !== "company") missing.push("DOB");
    if (!cd?.work_status || cd?.work_status === "unknown") missing.push("work status");
    if (!(cd?.body_parts && cd.body_parts.length) && !cd?.injury_illness) missing.push("injury");
    if (cd?.is_represented == null) missing.push("representation");
  }
  const mt = (claim?.matter_type || "ia").toUpperCase();

  // Parties
  const insuredP = parties.find(p => p.role === "Insured");
  const brokerP = parties.find(p => p.role === "Broker");
  const repP = parties.find(p => p.role === "Rep");
  const underwriters = parties.filter(p => p.role === "Underwriter" || p.role === "Lead Underwriter")
    .sort((a, b) => ((b.role === "Lead Underwriter" || b.is_lead) ? 1 : 0) - ((a.role === "Lead Underwriter" || a.is_lead) ? 1 : 0) || num(b.participation_pct) - num(a.participation_pct));
  const insuredName = insuredP?.company_name || claim?.insured_company_name || vca?.Insurer;
  const brokerName = brokerP?.company_name || vca?.Broker;

  // Policy
  const pp = pol?.attachments?.[0];
  const primaryInsured = pp?.insureds?.find(i => i.is_primary)?.company_name || pp?.insureds?.[0]?.company_name;
  const cov = pol?.coverages?.find(c => c.applies_to_loss) || pol?.coverages?.[0];
  const leadUW = pol?.slip?.find(s => s.is_lead) || pol?.slip?.[0];

  // WNW hours / WIP
  const totalHours = time.reduce((s, t) => s + num(t.hours), 0);
  const billableHours = time.filter(t => t.is_billable).reduce((s, t) => s + num(t.hours), 0);
  const isWip = (t) => t.is_billable && (t.status === "Unbilled" || t.status === "Pending Invoice Prep");
  const wipAmt = time.filter(isWip).reduce((s, t) => s + num(t.amount), 0);
  const billedAmt = time.filter(t => t.status === "Billed").reduce((s, t) => s + num(t.amount), 0);

  const railCardHdr = { background: "var(--navy)", color: "#fff", padding: "7px 12px", fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" };

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* ── Command rail — operational space (2026-07-02 Kuhrt: narrower; 2×3 actions) ── */}
      <div style={{ flex: "0 1 224px", minWidth: 200, display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={railCardHdr}>Quick Actions</div>
          <div style={{ padding: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <QBtn icon="📝" label="Note" onClick={() => goTab && goTab("notes")} />
            <QBtn icon="✓" label="Task" onClick={() => openTask && openTask({ claimId, claimLabel })} />
            <QBtn icon="⏱" label="Time" onClick={() => openTime && openTime({ claimId, claimLabel })} />
            <QBtn icon="📄" label="Document" onClick={() => goTab && goTab("documents")} />
            <QBtn icon="🧾" label="Bill" onClick={() => goTab && goTab("bills")} />
            <QBtn icon="💰" label="Reserve" onClick={() => goTab && goTab("reserves")} />
          </div>
        </div>

        <div className="card" style={{ overflow: "hidden" }}>
          <div style={railCardHdr}>Next Actions{attn.length > 0 ? ` · ${attn.length}` : ""}</div>
          <div style={{ padding: attn.length > 0 ? 0 : "10px 12px" }}>
            {attn.length > 0
              ? <ClaimAttentionPanel claimId={claimId} onGo={onAttnGo} refreshKey={workRefresh} bare />
              : <div style={{ fontSize: 12.5, color: "var(--green)" }}>✓ Nothing needs attention.</div>}
          </div>
        </div>

        <ClaimReadinessPanel claimId={claimId} hideWhenComplete />
      </div>

      {/* grey vertical divider between the command rail and the summary grid */}
      <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)", flexShrink: 0 }} />

      {/* ── Summary blocks: Activity = fixed narrow tall column; the rest grid to its right ── */}
      <div style={{ flex: "999 1 520px", minWidth: 0, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <OverviewTile title="Recent Activity" onOpen={() => goTab && goTab("feed")} minHeight={320}
          style={{ flex: "0 1 340px", minWidth: 280, maxHeight: 640 }}>
          {activity.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text3)", padding: "6px 0" }}>No activity yet on this claim.</div>
          ) : (<>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "2px 0 6px" }}>
              {ACT_FILTERS.filter(f => !f.types || activity.some(e => f.types.has(e.event_type)) || actFilter === f.id).map(f => {
                const n = f.types ? activity.filter(e => f.types.has(e.event_type)).length : activity.length;
                const on = actFilter === f.id;
                return (
                  <button key={f.id} onClick={(e) => { e.stopPropagation(); setActFilter(f.id); }}
                    style={{ padding: "2px 9px", fontSize: 10.5, borderRadius: 11, cursor: "pointer", fontWeight: on ? 700 : 500,
                             border: `1px solid ${on ? "var(--navy)" : "var(--border)"}`,
                             background: on ? "var(--navy)" : "#fff", color: on ? "#fff" : "var(--text2)" }}>
                    {f.label} <span style={{ opacity: 0.7 }}>({n})</span>
                  </button>
                );
              })}
            </div>
            <div style={{ overflowY: "auto", maxHeight: 520 }}>
            {(actFilter === "all" ? activity : activity.filter(e => (ACT_FILTERS.find(f => f.id === actFilter)?.types || new Set()).has(e.event_type)))
              .slice(0, 40).map((e, i) => {
                const meta = ACT_TYPE_META[e.event_type] || { label: e.event_type, color: "#888" };
                return (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "4px 0", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                    <span style={{ flexShrink: 0, background: meta.color, color: "#fff", fontSize: 8.5, padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, minWidth: 52, textAlign: "center", marginTop: 1 }}>{meta.label}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{e.title || "(no title)"}</span>
                    <span style={{ flexShrink: 0, fontSize: 10, color: "var(--text3)", marginTop: 1 }}>{e.occurred_at ? new Date(e.occurred_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
                  </div>
                );
              })}
            </div>
          </>)}
        </OverviewTile>

        <div style={{ flex: "1 1 520px", minWidth: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))", gap: 12, alignItems: "stretch" }}>

        <OverviewTile title="Claim Summary" onOpen={() => goTab && goTab("claimdetails")}>
          <KV label="Claim #">{claim?.claim_number}</KV>
          <KV label="Status"><StatusPill sm status={claim?.claim_status}>{claim?.claim_status}</StatusPill></KV>
          <KV label="Type">{claim?.claim_type_label || claim?.claim_type}</KV>
          <KV label="Matter"><StatusPill sm status={mt === "TPA" ? "tpa" : "ia"}>{mt}</StatusPill></KV>
          <KV label="Lead Adjuster">{claim?.lead_adjuster_name}</KV>
          <KV label="Vessel">{claim?.vessel_name}</KV>
          <KV label="Flags">{[claim?.is_jones_act && "Jones Act", claim?.litigation_flag && "Litigation", claim?.is_hipaa_sensitive && "HIPAA"].filter(Boolean).join(" · ") || <span style={{ color: "var(--text3)" }}>—</span>}</KV>
        </OverviewTile>

        <OverviewTile title="Claimant Summary" onOpen={() => goTab && goTab("claimant")}>
          <KV label="Claimant">{claimantName}{cd?.claimant_type === "company" && <span className="badge" style={{ marginLeft: 6, fontSize: 9 }}>ENTITY</span>}</KV>
          <KV label="DOB">{fmt.date(cd?.date_of_birth)}</KV>
          <KV label="Phone">{cd?.primary_phone || cd?.cell_phone}</KV>
          <KV label="Email">{cd?.email}</KV>
          <KV label="Address">{[cd?.address_line1, cd?.city, cd?.state_province, cd?.postal_code].filter(Boolean).join(", ")}</KV>
          <KV label="Injury">{(cd?.body_parts && cd.body_parts.length) ? cd.body_parts.join(", ") : (cd?.injury_illness || cd?.nature_of_injury)}</KV>
          <KV label="Work Status">{cd?.work_status ? <StatusPill sm status={cd.work_status}>{pretty(cd.work_status)}</StatusPill> : null}</KV>
          <KV label="Representation">{cd?.litigation_status ? <StatusPill sm status={cd.litigation_status}>{pretty(cd.litigation_status)}</StatusPill> : (cd?.is_represented != null ? yn(cd.is_represented) : null)}</KV>
          {missing.length > 0 && <div style={{ fontSize: 11, color: "#92400e", marginTop: 6 }}>⚠ Missing: {missing.join(", ")}</div>}
        </OverviewTile>

        <OverviewTile title="Loss Details" onOpen={() => goTab && goTab("claim")}>
          <KV label="Date of Loss">{fmt.date(claim?.date_of_loss)}</KV>
          <KV label="Location">{claim?.loss_location}</KV>
          <KV label="County / State">{[cd?.loss_county, claim?.loss_state].filter(Boolean).join(", ")}</KV>
          <KV label="Cause">{cd?.cause_of_loss}</KV>
          <KV label="Injury">{cd?.nature_of_injury || cd?.injury_illness}</KV>
          <KV label="Body Parts">{(cd?.body_parts && cd.body_parts.length) ? cd.body_parts.join(", ") : null}</KV>
        </OverviewTile>

        <OverviewTile title="Parties" onOpen={() => goTab && goTab("parties")}>
          <KV label="Insured">{insuredName}</KV>
          <KV label="Claimant">{claimantName}</KV>
          <KV label="Broker">{brokerName}</KV>
          {repP && <KV label="Rep">{[repP.contact_first_name, repP.contact_last_name].filter(Boolean).join(" ")}</KV>}
          {underwriters.length > 0 && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Underwriters / Slip</div>
              {underwriters.map(u => <Bubble key={u.id} pct={u.participation_pct} name={u.company_name} lead={u.role === "Lead Underwriter" || u.is_lead} />)}
            </div>
          )}
        </OverviewTile>

        <OverviewTile title="Policy Summary" onOpen={() => goTab && goTab("policy")}>
          {pp ? (<>
            <KV label="Policy #">{pp.policy_number}</KV>
            <KV label="Type">{pp.policy_type}{pp.layer_type ? ` · ${pp.layer_type}` : ""}</KV>
            <KV label="Period">{pp.inception_date ? `${fmt.date(pp.inception_date)} – ${fmt.date(pp.expiration_date)}` : null}</KV>
            <KV label="Insured">{primaryInsured}</KV>
            <KV label="Coverage">{cov ? `${cov.coverage_type || ""}${cov.coverage_amount ? " · " + fmt.money(cov.coverage_amount) : ""}` : null}</KV>
            <KV label="Deductible">{cov?.per_claim_deductible ? fmt.money(cov.per_claim_deductible) : null}</KV>
            <KV label="Lead UW">{leadUW?.company_name}</KV>
          </>) : (
            <div style={{ fontSize: 12, color: "var(--text3)", padding: "4px 0" }}>
              No policy attached. <button onClick={() => goTab && goTab("policy")} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0, fontWeight: 600 }}>Attach in Policy &amp; Coverage →</button>
            </div>
          )}
        </OverviewTile>

        <OverviewTile title="Claim Financials" onOpen={() => goTab && goTab("financials")}>
          <KV label="Total Reserve"><Money value={fin?.total ?? 0} /></KV>
          <KV label="Total Payments"><Money value={fin?.paid ?? 0} /></KV>
          <KV label="Remaining">{fin ? <span style={{ color: fin.rem < 0 ? "var(--red)" : "var(--green)", fontWeight: 700 }}><Money value={fin.rem} /></span> : "—"}</KV>
          <KV label="Open Bills">{openBills ? <StatusPill sm status="aging">{openBills}</StatusPill> : "—"}</KV>
        </OverviewTile>

        <OverviewTile title="WNW Hours & Fees" onOpen={() => goTab && goTab("timeexp")}>
          <KV label="Total Hours">{!timeLoaded ? "…" : (totalHours ? totalHours.toFixed(1) : "—")}</KV>
          <KV label="Billable Hours">{!timeLoaded ? "…" : (billableHours ? billableHours.toFixed(1) : "—")}</KV>
          <KV label="WIP (unbilled)">{!timeLoaded ? "…" : <Money value={wipAmt} />}</KV>
          <KV label="Billed Fees">{!timeLoaded ? "…" : <Money value={billedAmt} />}</KV>
        </OverviewTile>
        </div>
      </div>
    </div>
  );
}

// ── Activity (Feed / Notes / Tasks) ──────────────────────────────────────────
export function ActivityWorkspace({ claimId, claim, openTask, claimLabel, workRefresh, onWorkChanged, initialSub, focusTaskId }) {
  const [sub, setSub] = useState(initialSub || "feed");
  return (
    <div>
      <SubNav value={sub} onChange={setSub}
        options={[["feed", "Feed"], ["notes", "Notes / Diary"], ["tasks", "Tasks"]]} />
      {sub === "feed" && <ActivityTab claimId={claimId} />}
      {sub === "notes" && <DiaryTab claimId={claimId} claim={claim} onWorkChanged={onWorkChanged} />}
      {sub === "tasks" && <TasksTab claimId={claimId} openTask={openTask} claimLabel={claimLabel} workRefresh={workRefresh} onWorkChanged={onWorkChanged} focusTaskId={focusTaskId} />}
    </div>
  );
}

// ── Parties (+ Claimant & Loss) ──────────────────────────────────────────────
export function PartiesWorkspace({ claimId, claim, workRefresh, onWorkChanged }) {
  // CLAIMANT-INJURY-DASHBOARD-1 (2026-06-17): the claimant/loss/PHI blocks moved
  // OUT of Parties into the first-class Claimant tab. Parties now stays focused on
  // WNW staff / insured / broker / underwriters / contacts / delivery instructions.
  return <PartiesTab claimId={claimId} claim={claim} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />;
}

// ── Claim Info (CLAIM-OVERVIEW-UX-3) — container tab: Claim Details / Claimant /
//    Parties / Policy & Coverage as sub-tabs (folded from the old top tabs).
export function ClaimInfoWorkspace({ claimId, claim, vca, onClaimUpdated, workRefresh, onWorkChanged, initialSub }) {
  const [sub, setSub] = useState(initialSub || "claimdetails");
  return (
    <div>
      <SubNav value={sub} onChange={setSub} options={[
        ["claimdetails", "Claim"], ["claimant", "Claimant"],
        ["parties", "Parties"], ["policy", "Coverage"],
      ]} />
      {sub === "claimdetails" && <GeneralTab claim={claim} vca={vca} onClaimUpdated={onClaimUpdated} embedded onWorkChanged={onWorkChanged} />}
      {sub === "claimant" && <ClaimantTab claimId={claimId} claim={claim} onClaimUpdated={onClaimUpdated} onWorkChanged={onWorkChanged} />}
      {sub === "parties" && <PartiesTab claimId={claimId} claim={claim} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
      {sub === "policy" && <PolicyCoverageTab claimId={claimId} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
    </div>
  );
}

// ── Claim Financials summary card (CLAIM-OVERVIEW-UX-3) — expanded strip above
//    the Financials sub-nav. UW math (locked): Est UW Contribution = Total Reserve
//    − Deductible; Est Remaining to UW = UW Contribution − Funds Received. Collected
//    Deductible is not yet tracked natively (shows —).
export function ClaimFinancialsSummary({ claimId, refreshKey, isTpa, activeTab }) {
  const [r, setR] = useState(null);
  const [reimb, setReimb] = useState(null);
  const [pol, setPol] = useState(null);
  // UI-RIBBON-1 (LD-DESIGN-010): page-scoped extension cells — fetched only for
  // the active tab, only from endpoints that already exist (honesty rule).
  const [extra, setExtra] = useState(null);
  useEffect(() => {
    setExtra(null);
    const num = (v) => parseFloat(v || 0);
    if (activeTab === "parties") {
      apiFetch(`/claims/${claimId}/parties`).then(d => {
        const ps = d || [];
        const uw = ps.filter(p => p.role === "Underwriter" || p.role === "Lead Underwriter");
        setExtra({ kind: "parties",
          fin: ps.filter(p => ["Insured", "Rep", "Underwriter", "Lead Underwriter", "Broker"].includes(p.role)).length,
          uw: uw.length, pct: uw.reduce((s, u) => s + num(u.participation_pct), 0) });
      }).catch(() => setExtra(null));
    } else if (activeTab === "timelog") {
      apiFetch(`/claims/${claimId}/time`).then(d => {
        const t = d || [];
        setExtra({ kind: "time",
          hrs: t.reduce((s, x) => s + num(x.hours), 0),
          bill: t.filter(x => x.is_billable).reduce((s, x) => s + num(x.hours), 0),
          wip: t.filter(x => x.is_billable && (x.status === "Unbilled" || x.status === "Pending Invoice Prep")).reduce((s, x) => s + num(x.amount), 0) });
      }).catch(() => setExtra(null));
    } else if (activeTab === "activity") {
      apiFetch(`/claims/${claimId}/activity`).then(d => {
        const ev = d || [];
        setExtra({ kind: "activity", total: ev.length,
          notes: ev.filter(e => e.event_type === "note" || e.event_type === "diary").length });
      }).catch(() => setExtra(null));
    }
  }, [claimId, activeTab, refreshKey]);
  useEffect(() => { apiFetch(`/claims/${claimId}/reserves`).then(d => { const a = d?.reserves || []; setR({ total: a.reduce((s, x) => s + parseFloat(x.total_reserve || 0), 0), paid: a.reduce((s, x) => s + parseFloat(x.cum_paid || 0), 0), rem: a.reduce((s, x) => s + parseFloat(x.current_balance || 0), 0) }); }).catch(() => setR({ total: 0, paid: 0, rem: 0 })); }, [claimId, refreshKey]);
  useEffect(() => { apiFetch(`/claims/${claimId}/reimbursements`).then(d => setReimb(d?.summary || null)).catch(() => setReimb(null)); }, [claimId, refreshKey]);
  useEffect(() => { apiFetch(`/claims/${claimId}/policy-coverage`).then(setPol).catch(() => setPol(null)); }, [claimId, refreshKey]);
  if (!r) return null;
  const cov = pol?.coverages?.find(c => c.applies_to_loss) || pol?.coverages?.[0];
  const ded = cov?.per_claim_deductible != null ? parseFloat(cov.per_claim_deductible) : null;
  const fundsReq = reimb?.total_requested ?? 0;
  const fundsRec = reimb?.total_received ?? 0;
  const uwContribution = ded != null ? Math.max(0, r.total - ded) : null;
  const uwRemaining = uwContribution != null ? Math.max(0, uwContribution - fundsRec) : null;
  const m = (v) => v == null ? "—" : fmt.money(v);
  // Shared SummaryRibbon (UI-STANDARD-1) — dividers auto-inserted between cells.
  const cells = [
    <RibbonCell key="tr"  label="Total Reserve"     value={m(r.total)} color="var(--navy)" />,
    <RibbonCell key="tp"  label="Total Payments"    value={m(r.paid)}  color="var(--text2)" />,
    <RibbonCell key="rr"  label="Remaining Reserve" value={m(r.rem)}
                color={r.rem < 0 ? "var(--red)" : (r.rem === 0 ? "#d97706" : "var(--green)")} />,
    <RibbonCell key="ded" label="Deductible"        value={m(ded)} />,
    <RibbonCell key="cd"  label="Collected Ded."    value="—" />,
    // LD-DESIGN-010 per-tab extension cells (only when the tab's data has loaded).
    ...(extra?.kind === "parties" ? [
      <RibbonCell key="xp1" icon="👥" label="Financial Parties" value={String(extra.fin)} />,
      <RibbonCell key="xp2" label="Underwriters" value={String(extra.uw)} />,
      <RibbonCell key="xp3" label="UW %" value={`${Math.round(extra.pct)}%`} />,
    ] : []),
    ...(extra?.kind === "time" ? [
      <RibbonCell key="xt1" icon="⏱" label="Total Hours" value={extra.hrs.toFixed(1)} />,
      <RibbonCell key="xt2" label="Billable Hours" value={extra.bill.toFixed(1)} />,
      <RibbonCell key="xt3" label="WIP (Unbilled)" value={m(extra.wip)} color={extra.wip > 0 ? "var(--navy)" : undefined} />,
    ] : []),
    ...(extra?.kind === "activity" ? [
      <RibbonCell key="xa1" icon="🕘" label="Events" value={String(extra.total)} />,
      <RibbonCell key="xa2" label="Notes" value={String(extra.notes)} />,
    ] : []),
    // A2: TPA-only KPIs — hidden on IA matters where they're always empty/noisy.
    ...(isTpa ? [
      <RibbonCell key="uwc" label="Est. UW Contribution"  value={m(uwContribution)} />,
      <RibbonCell key="fq"  label="Funds Requested"       value={m(fundsReq)} />,
      <RibbonCell key="fr"  label="Funds Received"        value={m(fundsRec)} color={fundsRec > 0 ? "var(--green)" : undefined} />,
      <RibbonCell key="uwr" label="Est. Remaining to UW"  value={m(uwRemaining)} color="var(--navy)" />,
    ] : []),
  ];
  return <SummaryRibbon>{cells}</SummaryRibbon>;
}

// ── Financials (Reserves / Payments / Bills / Funds Requests / Invoicing) ──────
export function FinancialsWorkspace({
  claim, claimId, openTime, claimLabel, workRefresh, focusBillId, onWorkChanged, billsVisible, initialSub,
}) {
  const [sub, setSub] = useState(initialSub || (focusBillId ? "bills" : "reserves"));
  const isTpa = claim?.matter_type === "tpa";
  const opts = [];
  if (billsVisible) opts.push(["bills", "Bills"]);
  opts.push(["payments", "Payments"]);
  opts.push(["reserves", "Reserves"]);
  opts.push(["reimb", "Funds Requests"]);
  opts.push(["billing", "Invoicing"]);
  if (isTpa) opts.push(["tpafin", "TPA Financials"]);
  // UI-SWEEP-CLAIM (2026-07-02): the financial ribbon moved UP to ClaimDetail —
  // it now renders persistently on every claim tab, so this workspace no longer
  // renders its own copy (that would double it).
  return (
    <div>
      <SubNav value={sub} onChange={setSub} options={opts} />
      {sub === "reserves" && <ReservesTab claimId={claimId} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
      {sub === "payments" && <PaymentsTab claimId={claimId} claim={claim} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
      {sub === "bills" && billsVisible && <ClaimBillsTab claim={claim} focusBillId={focusBillId} onWorkChanged={onWorkChanged} />}
      {sub === "billing" && <BillingReadiness claim={claim} claimId={claimId} onWorkChanged={onWorkChanged} />}
      {sub === "reimb" && <><ReimbursementsPanel claim={claim} claimId={claimId} onWorkChanged={onWorkChanged} /><PaymentRequests claim={claim} claimId={claimId} onWorkChanged={onWorkChanged} /></>}
      {sub === "tpafin" && (
        <InfoPanel title="TPA Financials">
          TPA-side financials for this matter — TPA fee allocation, trust funding /
          money-in-flight, and underwriter reimbursement — will live here. Visible only
          on TPA matters (behind the ethical wall). Being built next.
        </InfoPanel>
      )}
    </div>
  );
}

// ── Reports (claim reports + cap-gated Bordereau) ────────────────────────────
export function ReportsWorkspace({ claimId, claim }) {
  // CLAIMANT-INJURY-DASHBOARD-1 (P7): claim reports stay as polished cards; the
  // form-heavy Bordereau editor moves into its OWN subtab — shown only when the
  // user can view bordereau AND this claim is actually BDX-eligible — instead of
  // sitting awkwardly full-width beneath the report cards. Not eligible / no cap
  // → no subtab at all, just the report cards.
  const [sub, setSub] = useState("reports");
  const [bdxEligible, setBdxEligible] = useState(false);
  const canBdx = can("view_bordereau_reports");
  useEffect(() => {
    if (!canBdx) { setBdxEligible(false); return; }
    apiFetch(`/claims/${claimId}/bordereau/editor`)
      .then(d => setBdxEligible(!!(d?.inclusion?.eligible)))
      .catch(() => setBdxEligible(false));
  }, [claimId, canBdx]);
  const showBdx = canBdx && bdxEligible;
  return (
    <div>
      {showBdx && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["reports", "Claim Reports"], ["bordereau", "Bordereau / Market Reports"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setSub(id)}
              style={{ padding: "5px 14px", fontSize: 12, fontWeight: 600, borderRadius: 14, cursor: "pointer",
                border: "1px solid " + (sub === id ? "var(--navy)" : "var(--border)"),
                background: sub === id ? "var(--navy)" : "transparent", color: sub === id ? "#fff" : "var(--text2)" }}>{lbl}</button>
          ))}
        </div>
      )}
      {sub === "reports" && <ClaimReportsTab claimId={claimId} claim={claim} />}
      {sub === "bordereau" && showBdx && <BordereauClaimTab claimId={claimId} />}
    </div>
  );
}
